import { observer } from "mobx-react";
import "./CaseItem.scss";
import { Case, CaseStatus } from "../../../../stores/CaseStore/types";
import CaseDetails from "../../../CaseDetails";
import useTranslation from "../../../../hooks/useTranslation";
import { cityMap } from "../../../../utils/cityCodes";
import { parseDate } from "../../../../utils/helperFunctions";
import { Button, ButtonGroup, Chip, CircularProgress } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import { UserRoles } from "../../../../stores/AuthStore/types";
import Delegation from "../../../Delegation";
import caseStore from "../../../../stores/CaseStore";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";

const CaseItem = observer(({ caseItem }: { caseItem: Case }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [privateCaseItem, setPrivateCaseItem] = useState(caseItem);

  const handleOnStatusChange = () => {
    const status =
      privateCaseItem.status === CaseStatus.ACTIVE
        ? CaseStatus.DEACTIVE
        : CaseStatus.ACTIVE;
    setIsLoading(true);
    caseStore
      .updateStatus([privateCaseItem.id], status)
      .then(() => {
        privateCaseItem.status = status;
      })
      .catch((error) => {
        console.log(error);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const afterCaseSubmitted = (updatedCase: Case, success: boolean) => {
    if (success) {
      setPrivateCaseItem({ ...privateCaseItem, ...updatedCase });
      setOpen(false);
    }
  };

  return (
    <div
      className={`case dashboardItem ${privateCaseItem.status.toLowerCase()}`}
    >
      <header className="pb">
        <span>{privateCaseItem.id}</span>
        <Chip
          variant={"outlined"}
          label={t(privateCaseItem.status.toLowerCase())}
        />
      </header>
      <main>
        <div className="labels">
          <label>{t("case-owner")}:</label>
          <label>{t("case-city")}:</label>
          <label>{t("budget")}:</label>
          <label>{t("create-date")}:</label>
        </div>
        <div className="values">
          <p>{t(privateCaseItem.ownerRole.toLowerCase())}</p>
          <p>{cityMap[privateCaseItem.caseProvince]}</p>
          <p>
            {privateCaseItem.budget
              ?.toString()
              .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1.")}
            {privateCaseItem.currency}
          </p>
          <p>{parseDate(privateCaseItem.createDate)}</p>
        </div>
      </main>
      <footer>
        <ButtonGroup
          fullWidth
          variant={
            privateCaseItem.status === CaseStatus.ACTIVE
              ? "outlined"
              : "contained"
          }
        >
          <Button onClick={() => setOpen(true)}>
            <i className="fal fa-edit" />
            &nbsp;
            {t("edit")}
          </Button>
          <Button
            color={
              privateCaseItem.status === CaseStatus.ACTIVE
                ? "error"
                : "secondary"
            }
            onClick={handleOnStatusChange}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={"16px"} />
            ) : (
              <>
                {privateCaseItem.status === CaseStatus.ACTIVE && (
                  <i className="fal fa-ban" />
                )}
                &nbsp;
                {privateCaseItem.status === CaseStatus.ACTIVE &&
                  t("deactivate")}
                {privateCaseItem.status !== CaseStatus.ACTIVE && t("activate")}
              </>
            )}
          </Button>
        </ButtonGroup>
      </footer>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="adminCaseItemDialog"
      >
        <DialogActions className="dialogActions">
          <Button onClick={() => setOpen(false)} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent>
          {privateCaseItem.ownerRole === UserRoles.USER ? (
            <CaseDetails
              caseItem={privateCaseItem}
              afterSubmit={afterCaseSubmitted}
            />
          ) : (
            <Delegation caseItem={privateCaseItem} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default CaseItem;
